import type { EmblaCarouselType } from 'embla-carousel';

import { useCallback, useEffect, useState } from 'react';
import Autoplay from 'embla-carousel-autoplay';
import useEmblaCarousel from 'embla-carousel-react';
import { twMerge } from 'tailwind-merge';

export interface CarouselProps {
  className?: string;
  autoPlay?: boolean;
  loop?: boolean;
  slides: React.ReactNode[];
}

export const useDotButton = (emblaApi?: EmblaCarouselType) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [scrollSnaps, setScrollSnaps] = useState<number[]>([]);

  const onDotButtonClick = useCallback(
    (index: number) => {
      if (!emblaApi) return;
      emblaApi.scrollTo(index);
    },
    [emblaApi],
  );

  const onInit = useCallback((emblaApi: EmblaCarouselType) => {
    setScrollSnaps(emblaApi.scrollSnapList());
  }, []);

  const onSelect = useCallback((emblaApi: EmblaCarouselType) => {
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, []);

  useEffect(() => {
    if (!emblaApi) return;
    onInit(emblaApi);
    onSelect(emblaApi);
    emblaApi.on('reInit', onInit).on('reInit', onSelect).on('select', onSelect);
  }, [emblaApi, onInit, onSelect]);

  return {
    selectedIndex,
    scrollSnaps,
    onDotButtonClick,
  };
};

export const Carousel: React.FC<CarouselProps> = ({ loop, autoPlay, className, slides }) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop }, autoPlay ? [Autoplay()] : []);
  const { selectedIndex, scrollSnaps, onDotButtonClick } = useDotButton(emblaApi);

  return (
    <div ref={emblaRef} className={twMerge('overflow-hidden', className)}>
      <div className="flex touch-pan-y">
        {slides.map((slide, index) => (
          <div
            className="transform-[translate3d(0,0,0)] min-w-0 flex-[0_0_100%] select-none"
            key={index}
          >
            {slide}
          </div>
        ))}
      </div>
      <footer className="mt-[30px] flex w-full items-center justify-center gap-1">
        {scrollSnaps.map((_, index) => (
          <div
            key={index}
            role="button"
            onClick={() => onDotButtonClick(index)}
            className={twMerge(
              'h-[6px] w-[6px] cursor-pointer rounded-[50%]',
              selectedIndex === index ? 'bg-blue' : 'bg-grey-mid',
            )}
          />
        ))}
      </footer>
    </div>
  );
};
